/**
 * @fileoverview The two questions asked right after a power bank upload:
 * "Do you want a hold for this item? Y/N" and, on yes, "How much is the
 * hold?". The answer is saved as the city's default and offered on every
 * handover on the pickup page.
 */
import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'

import { setPowerBankHoldSetting } from 'Module/graphQL'
import { formatHoldAmount, parseHoldAmountInput } from 'Utils/PowerBanks'

import PowerBankDialog from './PowerBankDialog'
import HoldAmountInput from './HoldAmountInput'
import { readGraphQLData } from './hooks'

const STEP_ASK = 'ask'
const STEP_AMOUNT = 'amount'

export const HOLD_SETTING_TITLE = 'Power bank hold'

const saveSetting = async ({ cityId, holdRequired, holdAmount }) => {
  const data = await readGraphQLData(
    await setPowerBankHoldSetting({ cityId, holdRequired, holdAmount }),
  )
  return data.setPowerBankHoldSetting
}

const HoldSettingPrompt = ({
  show,
  cityId,
  money,
  currentSetting,
  onSaved,
  onCancel,
}) => {
  const [step, setStep] = useState(STEP_ASK)
  const hasCurrentAmount =
    currentSetting && Number(currentSetting.holdAmount) > 0
  const [amountText, setAmountText] = useState(
    hasCurrentAmount ? String(currentSetting.holdAmount) : '',
  )
  const [amountError, setAmountError] = useState(null)

  const reset = useCallback(() => {
    setStep(STEP_ASK)
    setAmountError(null)
  }, [])

  const handleCancel = useCallback(() => {
    reset()
    onCancel()
  }, [reset, onCancel])

  const handleNo = useCallback(async () => {
    const saved = await saveSetting({
      cityId,
      holdRequired: false,
      holdAmount: 0,
    })
    reset()
    onSaved(saved)
  }, [cityId, reset, onSaved])

  const handleYes = useCallback(async () => {
    setStep(STEP_AMOUNT)
  }, [])

  const handleSaveAmount = useCallback(async () => {
    const { value, error } = parseHoldAmountInput(amountText)
    if (error) {
      setAmountError(error)
      return
    }
    setAmountError(null)
    const saved = await saveSetting({
      cityId,
      holdRequired: true,
      holdAmount: value,
    })
    reset()
    onSaved(saved)
  }, [amountText, cityId, reset, onSaved])

  const isAmountStep = step === STEP_AMOUNT

  // One dialog instance for both steps, so moving from "Yes" to the amount
  // box does not unmount the dialog while its confirm handler is finishing.
  return (
    <PowerBankDialog
      show={show}
      title={HOLD_SETTING_TITLE}
      confirmLabel={isAmountStep ? 'Save hold' : 'Yes'}
      secondaryLabel="No"
      onConfirm={isAmountStep ? handleSaveAmount : handleYes}
      onSecondary={isAmountStep ? null : handleNo}
      onCancel={handleCancel}
    >
      {isAmountStep ? (
        <div>
          <p>
            <strong>How much is the hold?</strong>
          </p>
          <p>
            This amount is held from a brand ambassador for each power bank they
            are given, and given back when the power bank is returned.
          </p>
          <HoldAmountInput
            value={amountText}
            onChange={setAmountText}
            error={amountError}
            money={money}
          />
        </div>
      ) : (
        <div>
          <p>
            <strong>Do you want a hold for this item?</strong>
          </p>
          <p>
            A hold is money kept from a brand ambassador while they have a power
            bank. Your answer becomes this city&apos;s default and is offered on
            every handover on the pickup page.
          </p>
          {hasCurrentAmount && (
            <p>
              The current default is{' '}
              <strong>
                {formatHoldAmount(currentSetting.holdAmount, money)}
              </strong>
              {currentSetting.holdRequired ? '' : ' (hold switched off)'}.
            </p>
          )}
        </div>
      )}
    </PowerBankDialog>
  )
}

HoldSettingPrompt.propTypes = {
  show: PropTypes.bool.isRequired,
  cityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  money: PropTypes.shape({
    currencySign: PropTypes.string,
    currency: PropTypes.string,
  }),
  currentSetting: PropTypes.shape({
    holdRequired: PropTypes.bool,
    holdAmount: PropTypes.number,
  }),
  /** Called with the saved CityInventoryHoldSetting. */
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

HoldSettingPrompt.defaultProps = {
  money: {},
  currentSetting: null,
}

export default HoldSettingPrompt
